// ChangeLocationDrawer — bottom sheet for changing the engineer's working
// postcode (DEV-48214). Slides up from the bottom, replacing the full-page hop
// to JobSetup.
//
// DEV-51046: the basket now survives a move, so the drawer no longer warns
// about losing it and no longer clears it. It says as much up front, and the
// toast on confirm reassures the engineer their lines are still there.
import { useState, type RefObject } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { MapPin, X, Loader2 } from 'lucide-react'

import { Button, useToast } from '../radix'
import { AtBranchCard } from './AtBranchCard'
import { RecentLocations } from './RecentLocations'
import { UseMyLocationButton } from './UseMyLocationButton'
import { useCart } from '../../auth/hooks/useCart'
import { useLocationChange } from '../../auth/hooks/useLocationChange'
import { useBranchesNearPostcode } from '../../hooks/useBranchesNearPostcode'
import { useDebounce } from '../../hooks/useDebounce'
import { useDeviceLocation } from '../../hooks/useDeviceLocation'
import { useCurrentAtBranches } from '../../hooks/useCurrentAtBranches'
import { useRecentLocations } from '../../hooks/useRecentLocations'
import { useSetEngineerPostcode } from '../../hooks/useSetEngineerPostcode'
import {
  POSTCODE_FAILURE_MESSAGES,
  searchPostcodes,
  normalizePostcode,
} from '../../services/postcodes'
import {
  isSameBranchSelection,
  resolveAtBranchesToSave,
} from '../../services/engineerSession'
import type { AtBranchSource } from '../../services/engineerSession'

export interface ChangeLocationDrawerProps {
  open: boolean
  onClose: () => void
  /** The engineer's current working postcode, shown on the "Keep" action. */
  currentPostcode: string
  /**
   * The control that opened the sheet, given focus back when it closes
   * (AC7). The sheet isn't opened through a Radix trigger, so without this
   * Radix has nowhere to return focus and it drops to the page.
   */
  returnFocusRef?: RefObject<HTMLElement | null>
}

const SUGGESTION_MIN_LENGTH = 2
const SUGGESTION_DEBOUNCE_MS = 300

// Ties the inline error to the field for screen readers. A constant id is safe:
// only one drawer is mounted at a time.
const POSTCODE_ERROR_ID = 'change-location-postcode-error'

/**
 * Inner form. Lives as a child of `Dialog.Content` so Radix unmounts it on
 * close, resetting the draft postcode / error / submit state for free (same
 * pattern as SearchFeedbackModal).
 */
function ChangeLocationForm({
  currentPostcode,
  onClose,
  isSubmitting,
  setIsSubmitting,
}: Omit<ChangeLocationDrawerProps, 'open'> & {
  isSubmitting: boolean
  setIsSubmitting: (value: boolean) => void
}) {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const { addToast } = useToast()
  const { cart } = useCart()
  const { markLocationChanged } = useLocationChange()
  const { setEngineerPostcode } = useSetEngineerPostcode()
  const { recentPostcodes } = useRecentLocations(currentPostcode)

  // Opens on the postcode in use so the engineer can correct a character
  // instead of retyping it. The form remounts on every open (it's a child of
  // `Dialog.Content`), so each open starts from the current value.
  const [postcode, setPostcode] = useState(currentPostcode)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // True while the postcode in the field came from the device rather than the
  // keyboard, so the "check it's right" hint shows.
  const [isPostcodeFromDevice, setIsPostcodeFromDevice] = useState(false)

  // The postcode in use and the nearby branches pinned for it, so the
  // branch card opens on what's pinned when the field holds that postcode.
  const currentAtBranches = useCurrentAtBranches()

  // "Use my current location". Fills the field only — the engineer still
  // confirms with the primary button.
  const {
    isSupported: isDeviceLocationSupported,
    status: deviceLocationStatus,
    failureReason: deviceLocationFailure,
    found: deviceLocationFound,
    atBranchIds,
    setAtBranchIds,
    locate: locateDevicePostcode,
    cancel: cancelDeviceLocation,
  } = useDeviceLocation(currentAtBranches)

  const debouncedPostcode = useDebounce(postcode, SUGGESTION_DEBOUNCE_MS)
  const { data: suggestions = [] } = useQuery({
    queryKey: ['postcode-autocomplete', debouncedPostcode],
    queryFn: () => searchPostcodes(debouncedPostcode),
    enabled: showSuggestions && debouncedPostcode.length >= SUGGESTION_MIN_LENGTH,
    staleTime: 30000,
    placeholderData: (previousData) => previousData,
  })

  const trimmedPostcode = postcode.trim()

  // Branches near the postcode in the field. Only consulted when the device
  // route has nothing on offer, so the two never compete for the card.
  //
  // Includes the current postcode the sheet opens with: otherwise there is no
  // way to choose branches for the postcode the engineer is already on (it
  // isn't in Recent, and retyping it doesn't count as a change). Same default
  // as everywhere else — a lone branch starts ticked (AC1) — so the card looks
  // the same however the postcode got into the field; "Keep" changes nothing.
  const {
    branches: postcodeBranches,
    selectedBranchIds: postcodeAtBranchIds,
    setSelectedBranchIds: setPostcodeAtBranchIds,
  } = useBranchesNearPostcode(
    postcode,
    deviceLocationFound === null,
    currentAtBranches,
  )

  // The device knows where the engineer is; a typed postcode only knows the
  // area. Wording downstream depends on which, so the source travels with it.
  const atBranchSource: AtBranchSource = deviceLocationFound
    ? 'device'
    : 'postcode'
  const offeredBranches = deviceLocationFound
    ? deviceLocationFound.branches
    : postcodeBranches
  const offeredSelectionIds = deviceLocationFound
    ? atBranchIds
    : postcodeAtBranchIds
  const setOfferedSelectionIds = deviceLocationFound
    ? setAtBranchIds
    : setPostcodeAtBranchIds

  // The branches to carry through on confirm: the ticked ones the card
  // showed, plus — for the postcode already in use — any pin it didn't show,
  // so a card still loading or a failed lookup never wipes pins.
  const confirmedAtBranches = resolveAtBranchesToSave(
    postcode,
    offeredBranches,
    offeredSelectionIds,
    atBranchSource,
    currentAtBranches,
  )

  const handleSelectSuggestion = (selected: string) => {
    setPostcode(selected)
    setShowSuggestions(false)
    setError(null)
    // Choosing a different postcode means the device result no longer
    // describes the field, so its hint and any counter card go with it.
    setIsPostcodeFromDevice(false)
    cancelDeviceLocation()
  }

  /**
   * Fill the postcode field from the device position. Nothing is applied: the
   * engineer checks it and presses the primary button. A result arriving after
   * the drawer closed is dropped by the hook, so it can't fill a form that is
   * no longer on screen.
   */
  const handleUseMyLocation = async () => {
    const foundPostcode = await locateDevicePostcode()
    if (!foundPostcode) {
      return
    }
    setPostcode(foundPostcode)
    setShowSuggestions(false)
    setError(null)
    setIsPostcodeFromDevice(true)
  }

  const handleConfirm = async () => {
    // Re-entry guard: ignore repeat calls (e.g. a second Enter) while a change
    // is already in flight, so two requests can't race and overwrite each other
    // with duplicate navigations / toasts.
    if (isSubmitting) {
      return
    }
    if (!trimmedPostcode) {
      setError('Please enter a postcode to continue')
      return
    }

    // Same postcode AND the same nearby branches, found the same way →
    // nothing to change: skip the backend save and the navigation, and just
    // close (same outcome as "Keep"). Also why an unchanged postcode shows no
    // toast (AC1).
    //
    // Anything else is saved, even on an unchanged postcode: whatever is
    // ticked becomes the nearby pins exactly (DEV-51045), so ticking a branch
    // or unticking a pinned one must reach the session. The card opens on
    // what's pinned, so confirming untouched still lands here and changes
    // nothing.
    if (
      normalizePostcode(trimmedPostcode) === normalizePostcode(currentPostcode) &&
      isSameBranchSelection(
        confirmedAtBranches,
        currentAtBranches?.branches ?? [],
      )
    ) {
      onClose()
      return
    }

    // Any error stays on screen while this check runs (LOC-03): clearing it
    // made the sheet shrink and then grow again on every press of the same
    // wrong postcode, so the sheet bounced. It is cleared as soon as the
    // engineer edits the field or picks a suggestion or recent postcode.
    setIsSubmitting(true)

    // Resolve + persist the new postcode. If it is invalid this returns early
    // having changed nothing.
    const result = await setEngineerPostcode(trimmedPostcode, confirmedAtBranches)
    if (!result.ok) {
      setError(POSTCODE_FAILURE_MESSAGES[result.failure ?? 'not_found'])
      setIsSubmitting(false)
      return
    }

    // Record the branch each basket line is at as of this move, so the cart
    // can flag the ones now left far away until they move or leave the
    // basket. A first-time set, an unchanged postcode, or a failed save (both
    // fields null) leave the record as it was.
    if (result.locationChanged === true) {
      markLocationChanged(cart)

      // Only worth saying when there was a basket to keep, so an empty one
      // stays silent (AC1). Plain wording, as the testing script expects; a
      // branch now far away is flagged in the basket itself ("Change branch").
      if ((result.itemCount ?? 0) > 0) {
        addToast(
          // The stored spelling, not the raw input, so the toast matches the
          // postcode pill the user lands back on ("SW100AD" → "SW10 0AD").
          `Searching near ${result.postcode}. Your basket is still here.`,
          'info',
          4000,
        )
      }
    }

    onClose()
    // Bounce through the engineer index so JobSetup re-lands on the search /
    // home screen and every page remounts against the new location. Reusing
    // that path guarantees a refetch even when the user was already on search.
    navigate(`/${tenantSlug}/engineer`)
  }

  const canSubmit = trimmedPostcode.length > 0 && !isSubmitting
  const primaryLabel = isSubmitting
    ? 'Updating…'
    : trimmedPostcode
      ? `Search near ${trimmedPostcode}`
      : 'Enter a postcode'

  return (
    <div className="flex flex-col gap-3">
      {/* Grab handle */}
      <span
        className="mx-auto mb-0.5 h-1.5 w-11 rounded-full bg-gray-300"
        aria-hidden="true"
      />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Dialog.Title className="text-lg font-bold text-gray-900">
            Change location
          </Dialog.Title>
          <p className="text-[13px] text-gray-500">Your basket stays as it is.</p>
        </div>
        <Dialog.Close asChild>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Enter a new postcode to search for products near a different location.
      </Dialog.Description>

      {/* Postcode input */}
      <div className="relative">
        <MapPin className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={postcode}
          onChange={(event) => {
            setPostcode(event.target.value.toUpperCase())
            setShowSuggestions(true)
            setError(null)
            // What the engineer types wins: drop a lookup still in flight so a
            // late answer can't overwrite it, and stop calling the value
            // "found from your location". `found` is covered too — the counter
            // lookup runs on after the postcode lands, and its result must not
            // reinstate a card for a postcode the engineer has moved away
            // from. A failure message is left alone: it points them at this
            // field, which is what they're using.
            if (
              isPostcodeFromDevice ||
              deviceLocationStatus === 'locating' ||
              deviceLocationStatus === 'found'
            ) {
              cancelDeviceLocation()
              setIsPostcodeFromDevice(false)
            }
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            // Defer so a tap on a suggestion (which blurs the input) still
            // registers before the list closes.
            window.setTimeout(() => setShowSuggestions(false), 100)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !isSubmitting) {
              setShowSuggestions(false)
              handleConfirm()
            }
          }}
          placeholder="Enter a postcode"
          autoComplete="off"
          disabled={isSubmitting}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? POSTCODE_ERROR_ID : undefined}
          className={`h-14 w-full rounded-2xl border-[1.5px] bg-white pl-12 pr-4 text-base font-semibold text-gray-900 placeholder:font-normal placeholder:text-gray-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
          }`}
        />

        {/* Floats under the field rather than sitting in the flow: everything
            below it — the location button, recent postcodes, and whatever the
            sheet grows next — must keep its place while the list opens and
            closes. */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-2xl border border-gray-200 bg-white px-4 py-2 shadow-lg">
            <span className="sticky top-0 block bg-white pb-2 pt-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">
              Suggestions
            </span>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                // onMouseDown (not onClick) so selection fires before the
                // input's onBlur closes the list.
                onMouseDown={(event) => {
                  event.preventDefault()
                  handleSelectSuggestion(suggestion)
                }}
                className="flex w-full items-center gap-3 border-b border-gray-100 py-3 text-left last:border-b-0 hover:bg-gray-50"
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary-lightest text-brand-primary">
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="text-[15px] font-semibold text-gray-900">
                  {suggestion}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error and hint sit directly under the field they describe. Anything
          further down is separated from the field by the suggestion list. */}
      {error && (
        <p id={POSTCODE_ERROR_ID} className="text-sm text-red-600">
          {error}
        </p>
      )}

      {/* Hint shown only while the field holds a postcode from the device. */}
      {isPostcodeFromDevice && !error && (
        <p className="text-sm text-gray-600">
          Found from your location. Check it&apos;s right.
        </p>
      )}

      <UseMyLocationButton
        isSupported={isDeviceLocationSupported}
        status={deviceLocationStatus}
        failureReason={deviceLocationFailure}
        onLocate={handleUseMyLocation}
      />

      {/* AC3: editing the postcode drops the device result, which takes this
          card with it. */}
      <AtBranchCard
        branches={offeredBranches}
        source={atBranchSource}
        selectedBranchIds={offeredSelectionIds}
        onChange={setOfferedSelectionIds}
      />

      {/* Tapping a recent postcode fills the field, exactly as picking a
          suggestion does: its nearby branches are offered and the engineer
          confirms. Replaces DEV-51044 AC6's "applies it at once". */}
      <RecentLocations
        postcodes={recentPostcodes}
        onSelect={handleSelectSuggestion}
        disabled={isSubmitting}
        headingLevel="h3"
      />

      {/* Actions — pinned to the bottom of the sheet's scroll area. Without
          this the confirm button scrolls away once the suggestion list and the
          phone keyboard are both up, which is most of the time on a phone.
          The negative margin lets the divider span the sheet's full width. */}
      <div className="sticky bottom-0 -mx-5 flex flex-col gap-2.5 border-t border-gray-100 bg-white px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        <Button
          variant="primary"
          size="lg"
          onClick={() => handleConfirm()}
          disabled={!canSubmit}
          className="h-14 w-full"
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {primaryLabel}
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={onClose}
          disabled={isSubmitting}
          className="w-full"
        >
          Keep {currentPostcode}
        </Button>
      </div>
    </div>
  )
}

/**
 * Bottom-sheet wrapper. Escape and tapping the overlay close the drawer (AC2)
 * via Radix's `onOpenChange` — but only while idle. Once a location change is
 * in flight, Escape / overlay dismissal is blocked: unmounting the form would
 * not cancel the async `handleConfirm`, so it would still change the location
 * and navigate away behind an apparently cancelled drawer.
 * The form mounts fresh each open (child of `Dialog.Content`).
 */
export function ChangeLocationDrawer({
  open,
  onClose,
  currentPostcode,
  returnFocusRef,
}: ChangeLocationDrawerProps) {
  // Lifted so both onOpenChange and the dismissal guards below can see it.
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isSubmitting) {
      onClose()
    }
  }

  const preventWhileSubmitting = (event: Event) => {
    if (isSubmitting) {
      event.preventDefault()
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          onEscapeKeyDown={preventWhileSubmitting}
          onPointerDownOutside={preventWhileSubmitting}
          onInteractOutside={preventWhileSubmitting}
          // Focus the sheet itself rather than the postcode field, so the
          // phone keyboard doesn't open over "Use my current location" — the
          // one option an engineer who doesn't know the postcode needs to see.
          // Focus still lands inside the dialog, so Escape and the focus trap
          // behave.
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            if (event.currentTarget instanceof HTMLElement) {
              event.currentTarget.focus()
            }
          }}
          // Back to the control that opened the sheet (AC7, LOC-13). Radix only
          // does this for its own trigger, which the pill isn't. Skipped when
          // that control has gone — e.g. after a successful change navigates
          // away — so Radix's default applies.
          onCloseAutoFocus={(event) => {
            const opener = returnFocusRef?.current
            if (opener?.isConnected) {
              event.preventDefault()
              opener.focus()
            }
          }}
          className="animate-slide-up-sheet fixed inset-x-0 bottom-0 z-[51] mx-auto max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white px-5 pb-0 pt-2 shadow-xl"
        >
          <ChangeLocationForm
            currentPostcode={currentPostcode}
            onClose={onClose}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
